import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import {
    ChevronDown,
    Layers,
    Map,
    Globe,
    X,
    Truck,
    Radio,
    Key,
    Box,
    Square,
    PieChart,
    Users,
    GitPullRequest,
    Grid,
    Moon,
    Sun,
    Briefcase,
    LogOut,
    AlertCircle,
    Maximize2,
    Shuffle,
    TrendingUp,
    HelpCircle,
    File,
    ChevronRight,
    Eye,
    EyeOff,
    Download,
    Bell,
    Plus,
    Edit,
    Trash,
    ChevronLeft,
    Search,
    Minimize2,
    ArrowLeft,
    ZoomIn,
    ZoomOut,
} from 'react-feather';
import OptimizedCustomIcon from './OptimizedCustomIcon';
import {
    getOptimizedIcon,
    OPTIMIZED_ICON_REGISTRY,
} from './optimizedIconRegistry';
import { ICON_NAMES } from './icon-constants';
const DEFAULT_ICON_SIZE = 24;

/**
 * Sized container that centres the icon glyph.
 * Width & height are driven by the `size` prop so the hit-area and
 * layout participation of every LytIcon instance is predictable.
 */
const IconContainer = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${(props) => props.$size || DEFAULT_ICON_SIZE}px;
    height: ${(props) => props.$size || DEFAULT_ICON_SIZE}px;
    flex-shrink: 0;
    line-height: 0;
`;
export { ICON_NAMES };
const ICON_MAP = {
    'chevron-down': ChevronDown,
    layers: Layers,
    map: Map,
    globe: Globe,
    x: X,
    truck: Truck,
    radio: Radio,
    key: Key,
    box: Box,
    square: Square,
    'pie-chart': PieChart,
    grid: Grid,
    'git-pull-request': GitPullRequest,
    users: Users,
    briefcase: Briefcase,
    'alert-circle': AlertCircle,
    'maximize-2': Maximize2,
    'minimize-2': Minimize2,
    shuffle: Shuffle,
    'trending-up': TrendingUp,
    file: File,
    sun: Sun,
    moon: Moon,
    'log-out': LogOut,
    'help-circle': HelpCircle,
    'chevron-right': ChevronRight,
    'chevron-left': ChevronLeft,
    eye: Eye,
    'eye-off': EyeOff,
    download: Download,
    bell: Bell,
    plus: Plus,
    edit: Edit,
    trash: Trash,
    search: Search,
    'arrow-left': ArrowLeft,
    'zoom-in': ZoomIn,
    'zoom-out': ZoomOut,
};
const LytIcon = ({
    name = null,
    size = 24,
    url = null,
    color = null,
    alt = null,
    className = null,
    style = null,
}) => {
    const theme = useTheme();
    const isCustomSize = size !== DEFAULT_ICON_SIZE;

    // If URL is provided, render SVG from URL
    if (url) {
        return (
            <IconContainer $size={size} className={className} style={style}>
                <img
                    src={url}
                    alt={alt || 'Icon'}
                    {...(isCustomSize && {
                        style: {
                            width: size,
                            height: size,
                        },
                    })}
                />
            </IconContainer>
        );
    }

    // Check for optimized icons first (better performance)
    const optimizedIcon = getOptimizedIcon(name);
    if (optimizedIcon) {
        // For complex SVGs with masks/clips, render as img for proper display
        if (optimizedIcon.useAsImage && optimizedIcon.component) {
            // Get the SVG source - component is the imported SVG path
            const svgSrc =
                typeof optimizedIcon.component === 'string'
                    ? optimizedIcon.component
                    : optimizedIcon.component;
            return (
                <IconContainer $size={size} className={className} style={style}>
                    <img
                        src={svgSrc}
                        alt={name}
                        {...(isCustomSize && {
                            style: {
                                width: size,
                                height: size,
                            },
                        })}
                    />
                </IconContainer>
            );
        }
        return (
            <IconContainer $size={size} className={className} style={style}>
                <OptimizedCustomIcon
                    SvgComponent={optimizedIcon.component}
                    size={size}
                    color={color}
                    lightThemeFill={optimizedIcon.lightThemeFill}
                    darkThemeFill={optimizedIcon.darkThemeFill}
                    preserveColors={optimizedIcon.preserveColors}
                />
            </IconContainer>
        );
    }

    // Legacy custom SVG icons removed - all icons should use optimized registry

    // Handle React Feather icons
    const IconComponent = ICON_MAP[name];
    if (!IconComponent) {
        console.warn(
            `LytIcon: Icon "${name}" not found in ICON_MAP or optimized registry`
        );
        return null;
    }

    // Determine default color based on theme if no color is provided
    const defaultColor = color || theme?.icon?.default || '#000000';
    return (
        <IconContainer $size={size} className={className} style={style}>
            <IconComponent size={size} color={defaultColor} />
        </IconContainer>
    );
};
LytIcon.propTypes = {
    name: PropTypes.oneOf(Object.values(ICON_NAMES)),
    url: PropTypes.string,
    // URL to SVG file
    size: PropTypes.number,
    color: PropTypes.string,
    // Optional - defaults to theme-based color
    alt: PropTypes.string,
    // Alt text for URL-based icons
    className: PropTypes.string,
    style: PropTypes.object,
};
export default LytIcon;
